import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/client/components/ui/alert-dialog';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

type ShadowPeriod = NonNullable<RouterOutputs['shadowTimesheets']['get']>;
type ShadowEntry = ShadowPeriod['entries'][number];

interface ShadowEditPanelProps {
  userId: string;
  projectId: string;
  periodFrom: string;
  periodTo: string;
  onUserReassigned: (newUserId: string) => void;
}

function fmtDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function EntryRow({
  entry,
  onDelete,
  onSave,
}: {
  entry: ShadowEntry;
  onDelete: (id: string) => void;
  onSave: (id: string, date: string, description: string, hours: number) => void;
}) {
  const descRef = useRef<HTMLInputElement>(null);
  const hoursRef = useRef<HTMLInputElement>(null);

  function save() {
    const desc = descRef.current?.value ?? entry.description;
    const h = Number.parseFloat(hoursRef.current?.value ?? '') || entry.hours;
    onSave(entry.id, entry.date, desc, h);
  }

  return (
    <div className="grid grid-cols-[64px_1fr_80px_40px] gap-2 items-center px-4 py-2.5 border-b last:border-0">
      <div className="tabular-nums text-xs text-muted-foreground">{fmtDate(entry.date)}</div>
      <input
        ref={descRef}
        defaultValue={entry.description}
        key={`${entry.id}-desc`}
        onBlur={save}
        className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs outline-none w-full transition-colors"
      />
      <input
        ref={hoursRef}
        defaultValue={entry.hours.toFixed(1)}
        key={`${entry.id}-hours`}
        type="number"
        min="0.25"
        max="24"
        step="0.25"
        onBlur={save}
        className="bg-transparent border border-transparent hover:border-border focus:border-border rounded px-1.5 py-0.5 text-xs tabular-nums text-right outline-none w-full transition-colors"
      />
      <button
        type="button"
        onClick={() => onDelete(entry.id)}
        className="flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
        aria-label="Delete entry"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function ShadowEditPanel({ userId, projectId, periodFrom, periodTo, onUserReassigned }: ShadowEditPanelProps) {
  const utils = trpc.useUtils();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [addDate, setAddDate] = useState(periodFrom);
  const [addDesc, setAddDesc] = useState('');
  const [addHours, setAddHours] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const enabled = !!userId && !!projectId;

  const { data: period, isLoading } = trpc.shadowTimesheets.get.useQuery(
    { user_id: userId, project_id: projectId, period_from: periodFrom, period_to: periodTo },
    { enabled },
  );

  const { data: employees = [] } = trpc.shadowTimesheets.listEmployees.useQuery();

  const { data: targetPeriods, isLoading: targetPeriodsLoading } =
    trpc.shadowTimesheets.listByUserProject.useQuery(
      { user_id: reassignUserId, project_id: projectId },
      { enabled: reassignOpen && !!reassignUserId },
    );

  const targetConflictPeriod = (targetPeriods ?? []).find(
    (p) => p.period_from === periodFrom && p.period_to === periodTo,
  );

  function invalidate() {
    utils.shadowTimesheets.get.invalidate();
    utils.shadowTimesheets.diff.invalidate();
    utils.shadowTimesheets.listByUserProject.invalidate();
  }

  const createFromReplica = trpc.shadowTimesheets.createFromReplica.useMutation({ onSuccess: invalidate });
  const createBlank = trpc.shadowTimesheets.createBlank.useMutation({ onSuccess: invalidate });
  const upsertEntry = trpc.shadowTimesheets.upsertEntry.useMutation({ onSuccess: invalidate });
  const deleteEntry = trpc.shadowTimesheets.deleteEntry.useMutation({ onSuccess: invalidate });
  const deletePeriod = trpc.shadowTimesheets.deletePeriod.useMutation({
    onSuccess: () => {
      invalidate();
      setDeleteOpen(false);
    },
  });

  const reassignUser = trpc.shadowTimesheets.reassignUser.useMutation({
    onSuccess: (data) => {
      invalidate();
      onUserReassigned(data.user_id);
      setReassignOpen(false);
      setReassignUserId('');
      setReassignError(null);
    },
    onError: (err) => setReassignError(err.message),
  });

  const mergeEntries = trpc.shadowTimesheets.mergeEntries.useMutation({
    onSuccess: () => {
      invalidate();
      setReassignOpen(false);
      setReassignUserId('');
      setReassignError(null);
    },
    onError: (err) => setReassignError(err.message),
  });

  const mutInput = { user_id: userId, project_id: projectId, period_from: periodFrom, period_to: periodTo };

  function handleSaveEntry(id: string, date: string, description: string, hours: number) {
    if (!period) return;
    upsertEntry.mutate({ period_id: period.id, id, date, description, hours });
  }

  function handleAddEntry() {
    if (!period) return;
    const h = Number.parseFloat(addHours);
    if (!addDesc.trim()) { setAddError('Description required'); return; }
    if (Number.isNaN(h) || h <= 0 || h > 24) { setAddError('Enter valid hours (0.25–24)'); return; }
    setAddError(null);
    upsertEntry.mutate(
      { period_id: period.id, date: addDate, description: addDesc.trim(), hours: h },
      { onSuccess: () => { setAddDesc(''); setAddHours(''); } },
    );
  }

  if (!userId || !projectId) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">Select an employee and project.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!period) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            No shadow timesheet for this period.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => createFromReplica.mutate(mutInput)}
              disabled={createFromReplica.isPending}
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Replicate from real
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => createBlank.mutate(mutInput)}
              disabled={createBlank.isPending}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Start blank
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b px-6 py-3 shrink-0">
        <p className="text-sm font-semibold">
          Shadow · {periodFrom} – {periodTo}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {period.entries.length} entries
          </span>
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setReassignUserId(''); setReassignOpen(true); }}
          >
            Change user
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            Delete shadow
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-[64px_1fr_80px_40px] gap-2 border-b px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <div>Date</div>
          <div>Description</div>
          <div className="text-right">Hours</div>
          <div />
        </div>

        {period.entries.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No entries yet.</div>
        ) : (
          period.entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              onDelete={(id) => deleteEntry.mutate({ id })}
              onSave={handleSaveEntry}
            />
          ))
        )}

        {/* Add entry row */}
        <div className="border-t p-4">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Add entry</p>
          <div className="grid grid-cols-[120px_1fr_80px_auto] gap-2 items-start">
            <Input
              type="date"
              value={addDate}
              min={periodFrom}
              max={periodTo}
              onChange={(e) => setAddDate(e.target.value)}
              className="h-8 text-xs"
            />
            <Input
              placeholder="Description…"
              value={addDesc}
              onChange={(e) => setAddDesc(e.target.value)}
              className="h-8 text-xs"
            />
            <Input
              type="number"
              placeholder="Hours"
              min="0.25"
              max="24"
              step="0.25"
              value={addHours}
              onChange={(e) => setAddHours(e.target.value)}
              className="h-8 text-xs tabular-nums"
            />
            <Button size="sm" className="h-8" onClick={handleAddEntry} disabled={upsertEntry.isPending}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          {addError && <p className="mt-1 text-xs text-destructive">{addError}</p>}
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete shadow timesheet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the shadow and all its entries. Real timesheet data is
              unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={deletePeriod.isPending}
              onClick={() => deletePeriod.mutate({ id: period.id })}
            >
              {deletePeriod.isPending ? 'Deleting…' : 'Delete shadow'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={reassignOpen}
        onOpenChange={(open) => {
          setReassignOpen(open);
          if (!open) setReassignError(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change user</AlertDialogTitle>
            <AlertDialogDescription>
              Reassign this shadow timesheet to a different employee.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 py-2 space-y-3">
            <Select
              value={reassignUserId}
              onValueChange={(v) => {
                setReassignUserId(v);
                setReassignError(null);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select employee…" />
              </SelectTrigger>
              <SelectContent>
                {employees
                  .filter((e) => e.id !== userId)
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.full_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {reassignUserId && targetConflictPeriod && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400">
                This person already has a shadow for this period. Confirming will copy{' '}
                <span className="font-semibold">{targetConflictPeriod.entry_count} entries</span> into
                their shadow. Their existing entries are kept, and this shadow stays unchanged.
              </div>
            )}
            {reassignError && (
              <p className="text-xs text-destructive">{reassignError}</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              disabled={
                !reassignUserId ||
                targetPeriodsLoading ||
                reassignUser.isPending ||
                mergeEntries.isPending
              }
              onClick={() => {
                if (targetConflictPeriod) {
                  mergeEntries.mutate({
                    source_period_id: period.id,
                    target_user_id: reassignUserId,
                  });
                } else {
                  reassignUser.mutate({ period_id: period.id, new_user_id: reassignUserId });
                }
              }}
            >
              {reassignUser.isPending || mergeEntries.isPending
                ? 'Saving…'
                : targetConflictPeriod
                  ? 'Merge'
                  : 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
